/**
 * Phone Number Utilities for Australian & International Formats
 * 
 * Rules:
 * 1. Mobile numbers: displayed as 04xx xxx xxx (e.g. 0499 999 999)
 * 2. Non-mobile numbers (landlines): displayed as 0x xxxx xxxx (e.g. 02 9999 9999 for NSW, 03 for Vic, 07 for Qld, 08 for SA/WA/NT)
 * 3. Stored in E.164 format: +61499999999 (or +61299999999)
 */

/**
 * Format any phone number for read-only / uneditable display
 * @param {string|number|null|undefined} raw
 * @returns {string} Formatted phone number (e.g. "0414 749 350" or "02 9876 5432")
 */
export function formatPhoneNumber(raw) {
  if (!raw && raw !== 0) return "";
  const str = String(raw).trim();
  if (!str) return "";

  // Strip all non-digit and non-plus characters
  const cleaned = str.replace(/[^\d+]/g, "");
  if (!cleaned) return str;

  // Convert international +61 or 61 to national starting with 0
  let national = cleaned;
  if (national.startsWith("+61")) {
    national = "0" + national.slice(3);
  } else if (national.startsWith("61") && national.length === 11) {
    national = "0" + national.slice(2);
  } else if (!national.startsWith("0") && !national.startsWith("+") && (national.length === 9 || national.length === 10)) {
    national = "0" + national;
  }

  // Pure digits for national formatting
  const digits = national.replace(/\D/g, "");

  // Australian Mobile: starts with 04, 10 digits -> 04xx xxx xxx
  if (digits.startsWith("04") && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }

  // Australian Non-mobile / Landline: starts with 02, 03, 07, 08, 10 digits -> 0x xxxx xxxx
  if (/^0[2378]/.test(digits) && digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6, 10)}`;
  }

  // Australian Mobile in-progress or with prefix
  if (digits.startsWith("04")) {
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }

  // Australian Landline in-progress
  if (/^0[2378]/.test(digits)) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6, 10)}`;
  }

  // 1300 / 1800 numbers (6 digits or 10 digits)
  if (/^1[38]00/.test(digits) && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }
  if (digits.startsWith("13") && digits.length === 6) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}`;
  }

  // If already international starting with +, return as is
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  return str;
}

/**
 * Convert any Australian or international phone number to stored E.164 format (+61...)
 * @param {string|number|null|undefined} raw
 * @returns {string} E.164 formatted string (e.g. "+61414749350")
 */
export function toE164(raw) {
  if (!raw && raw !== 0) return "";
  const str = String(raw).trim();
  if (!str) return "";

  // If already valid E.164 (starts with +)
  if (str.startsWith("+")) {
    const digits = str.replace(/\D/g, "");
    return "+" + digits;
  }

  const digits = str.replace(/\D/g, "");
  if (!digits) return "";

  // Starts with 61 and 11 digits (e.g. 61414749350)
  if (digits.startsWith("61") && digits.length === 11) {
    return "+" + digits;
  }

  // Starts with 0 and 10 digits (e.g. 0414749350 or 0298765432) -> +61 + slice(1)
  if (digits.startsWith("0") && digits.length === 10) {
    return "+61" + digits.slice(1);
  }

  // 9 digits without leading 0 (e.g. 414749350 or 298765432) -> +61 + digits
  if (digits.length === 9 && !digits.startsWith("0")) {
    return "+61" + digits;
  }

  // Fallback: if starts with 0
  if (digits.startsWith("0")) {
    return "+61" + digits.slice(1);
  }

  return "+61" + digits;
}

/**
 * Format for editing input box (National representation: "0499 999 999" or "02 9999 9999")
 * @param {string} raw
 * @returns {string}
 */
export function toNationalDisplay(raw) {
  return formatPhoneNumber(raw);
}
